import React from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';

const VenueTermsStep = ({ venueTermsAccepted, setVenueTermsAccepted }) => {
    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-medium text-white mb-4">Venue Terms & Conditions</h3>
                <p className="text-gray-400 mb-6">
                    Please review and accept our venue partnership terms to continue.
                </p>
            </div>

            <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 max-h-96 overflow-y-auto">
                <h4 className="text-white font-medium mb-4">World's High Street - Venue Partnership Agreement</h4>
                <div className="text-gray-300 text-sm space-y-4">
                    <div>
                        <h5 className="font-medium text-white mb-2">1. Venue Partnership</h5>
                        <p>
                            By accepting these terms, you agree to become a venue partner on the World's High Street platform. 
                            You will be responsible for managing your venue's classes, sessions, and user interactions through our platform.
                        </p>
                    </div>
                    
                    <div>
                        <h5 className="font-medium text-white mb-2">2. Payment Processing</h5>
                        <p>
                            All payments will be processed through our integrated payment system using GoCardless. 
                            You agree to the payment terms and processing fees as outlined in your subscription plan.
                        </p>
                    </div>
                    
                    <div>
                        <h5 className="font-medium text-white mb-2">3. User Management</h5>
                        <p>
                            Your subscription plan includes a specific number of active users. You are responsible for 
                            managing your user count within the limits of your chosen plan. Additional users may require 
                            a plan upgrade.
                        </p>
                    </div>
                    
                    <div>
                        <h5 className="font-medium text-white mb-2">4. Data and Privacy</h5>
                        <p>
                            You agree to comply with all applicable data protection laws and regulations. 
                            We will handle user data in accordance with our privacy policy and GDPR requirements.
                        </p>
                    </div>
                    
                    <div>
                        <h5 className="font-medium text-white mb-2">5. Service Availability</h5>
                        <p>
                            We strive to maintain 99.9% uptime for our platform. However, we cannot guarantee 
                            uninterrupted service and will not be liable for brief service interruptions.
                        </p>
                    </div>
                    
                    <div>
                        <h5 className="font-medium text-white mb-2">6. Termination</h5>
                        <p>
                            Either party may terminate this agreement with 30 days written notice. 
                            Upon termination, you will retain access to your data for 90 days for export purposes.
                        </p>
                    </div>
                    
                    <div>
                        <h5 className="font-medium text-white mb-2">7. Support and Training</h5>
                        <p>
                            We provide support and training materials to help you get started with the platform. 
                            Support level depends on your subscription tier.
                        </p>
                    </div>
                    
                    <div>
                        <h5 className="font-medium text-white mb-2">8. Platform Fees</h5>
                        <p>
                            Subscription fees are charged monthly in advance. Payment processing fees may apply 
                            to transactions processed through the platform. All fees are clearly outlined in your 
                            subscription plan.
                        </p>
                    </div>
                </div>
            </div>

            <div className="space-y-4">
                <label className="flex items-start space-x-3 cursor-pointer">
                    <div className="flex-shrink-0">
                        <input
                            type="checkbox"
                            checked={venueTermsAccepted}
                            onChange={(e) => setVenueTermsAccepted(e.target.checked)}
                            className="sr-only"
                        />
                        <div className={`w-5 h-5 border-2 rounded flex items-center justify-center transition-colors ${
                            venueTermsAccepted 
                                ? 'bg-emerald-500 border-emerald-500' 
                                : 'border-gray-400 hover:border-gray-300'
                        }`}>
                            {venueTermsAccepted && (
                                <CheckIcon className="h-3 w-3 text-white" />
                            )}
                        </div>
                    </div>
                    <div className="text-sm">
                        <span className="text-gray-300">
                            I have read, understood, and agree to the{' '}
                            <span className="text-emerald-400 font-medium">
                                World's High Street Venue Partnership Terms & Conditions
                            </span>
                        </span>
                    </div>
                </label>
            </div>

            <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                    <div className="text-blue-400 mt-0.5">
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                    </div>
                    <div>
                        <h4 className="text-blue-400 font-medium text-sm">Important Note</h4>
                        <p className="text-blue-300 text-sm mt-1">
                            By proceeding, you agree to create a venue account and will be responsible for managing 
                            your venue's operations through our platform. You can review the full terms and conditions 
                            at any time from your dashboard.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VenueTermsStep;
